import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useCallback, useContext, useState } from 'react';
import { Identities } from '../../../model/store/providers/Identities';
import { LocalSystemConfig, LocalSystems, localSystems } from '../../../model/localSystems';
import { LinkAccountModal } from './LinkAccountModal';
import { map } from '../../../util/map';
import { UnlinkAccountModal } from './UnlinkAccountModal';
import { find } from '../../../util/find';
import { Referrer } from '../../../model/store/providers/Referrer';
import { LocalSystemFilter } from '../../../model/store/providers/LocalSystemFilter';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Localization } from '../../../model/store/providers/Localization';
import './MainPage.scss';
import { LocalIdentities } from '../../../model/types';
import { resourcesBaseUri, scopes } from '../../../config';
import { GeneralSection } from './GeneralSection';
import { MfaSection } from './MfaSection';

const MainPage = () => {
	const { referrerLabel, referrerUrl } = useContext(Referrer.State);
	const { enabledLocalSystems } = useContext(LocalSystemFilter.State);
	const { getAccessTokenSilently } = useAuth0();
	const identities = useContext(Identities.State);
	const [selectedSystem, setSelectedSystem] = useState<LocalSystemConfig>(localSystems.rba);
	const [selectedLocalId, setSelectedLocalId] = useState('');
	const [linkAccountModalOpen, setLinkAccountModalOpen] = useState(false);
	const [unlinkAccountModalOpen, setUnlinkAccountModalOpen] = useState(false);
	const navigate = useNavigate()
	const { translations } = useContext(Localization.State);
	const {
		mainPage: {
			localProfiles: {
				title
			}
		},
		backToReferrerBtn: {
			back,
			to,
			alt
		}
	} = translations;
	const referrerButtonText = referrerLabel ? `${back} ${to} ${referrerLabel}` : back;

	const linkIdentity = useCallback(async (selectedSystem: LocalSystemConfig) => {
		try {
			await getAccessTokenSilently({ scope: scopes });

			setSelectedSystem(selectedSystem);
			setLinkAccountModalOpen(true);
		} catch (e) {
			navigate('/login');
		}
	}, [navigate, getAccessTokenSilently, setSelectedSystem]);

	const closeLinkAccountModal = useCallback(() => {
		setLinkAccountModalOpen(false);
	}, []);

	const closeUnlinkAccountModal = useCallback(() => {
		setUnlinkAccountModalOpen(false);
	}, []);

	const unlinkIdentity = useCallback(async (systemId: LocalSystems, localAccountId: string) => {
		try {
			await getAccessTokenSilently({ scope: scopes });

			const [, system] = find(localSystems, (id) => id === systemId) ?? [];

			if (!system) return console.error(`Unknown system: ${systemId}`);

			setSelectedSystem(system);
			setSelectedLocalId(localAccountId);
			setUnlinkAccountModalOpen(true);
		} catch (e) {
			navigate('/login');
		}
	}, [navigate, getAccessTokenSilently, setSelectedSystem]);

	return <>
		<LinkAccountModal
			isOpen={linkAccountModalOpen}
			system={selectedSystem}
			closeModal={closeLinkAccountModal}
		/>
		<UnlinkAccountModal
			isOpen={unlinkAccountModalOpen}
			system={selectedSystem}
			localAccountId={selectedLocalId}
			closeModal={closeUnlinkAccountModal}
		/>
		<div className="outer-container">
			<Header />
			<div className="main-page">
				<div className="main-page__back">
					<a href={referrerUrl} className="main-page__back-link">
						<img
							className="main-page__back-icon"
							src={`${resourcesBaseUri}/icon-back.svg`}
							alt={alt}
						/>
						{referrerButtonText}
					</a>
				</div>
				<GeneralSection />
				<MfaSection />
				<h1 className="main-page__title">{title}</h1>
				<div className="main-page__body">
					<div className="main-page__profile">
						<div className="profile__body">
							{map(localSystems, (systemId, system) => {
								if(!enabledLocalSystems[systemId]) return null;
								return (
									<System
										key={systemId}
										system={system}
										identities={identities}
										unlinkIdentity={unlinkIdentity}
										linkIdentity={linkIdentity}
									/>
								);
							})}
						</div>
					</div>
					{/* LEFT IN FOR MULTIPLE ACCOUNT LINKING
					<LinkedIdentities identities={identities} unlinkIdentity={unlinkIdentity} /> */}
				</div>
			</div>
		</div>
		<Footer />
	</>;
}

type SystemProps = {
	system: LocalSystemConfig;
	identities: Partial<LocalIdentities>;
	unlinkIdentity: (systemId: LocalSystems, userId: string) => void;
	linkIdentity: (systemId: LocalSystemConfig) => void;
};

function System({ system, identities, unlinkIdentity, linkIdentity }: SystemProps) {
	const id = identities[system.id]?.[0]?.id ?? '';
	const { translations } = useContext(Localization.State);
	const {
		mainPage: {
			localProfiles: {
				linkBtn,
				unlinkBtn
			}
		}
	} = translations;

	if(id) {
		return (
			<div className="section__item">
				<img className={"local-id__system-" + system.id} src={localSystems[system.id].icon} alt={`${system.id} logo`} />
				<span className="local-id__id">{id}</span>
				<img className="identities__check" alt="check" src={`${resourcesBaseUri}/green-check.svg`} />
				<span className="wedge" />
				<span
					className="section__secondary-link"
					onClick={() => unlinkIdentity(system.id, id)}
				>{unlinkBtn}</span>
			</div>
		)
	}

	return (
		<div className="section__item">
			<img className={"local-id__system-" + system.id} alt="check" src={system.icon} />
			<div
				className="profile__link-identity-text"
				onClick={() => linkIdentity(system)}
			>
				{linkBtn}
			</div>
		</div>
	)

}
//LEFT IN FOR MULTIPLE ACCOUNT LINKING

// function LinkedIdentities({ identities, unlinkIdentity }) {
//     if (!isPopulated(identities)) return null;

	// return (
	//     <div className="main-page__identities">
	//         <h2 className="identities__title">Linked to</h2>
	//         <div className="identities__body">
	//             {map(identities, (systemId, localIds) => (
	//                 <div
	//                     key={systemId}
	//                     className="identities__system"
	//                 >
	//                     {localIds.map(({ id }) => (
	//                         <div key={id} className="identities__local-id">
	//                             <img className="local-id__system" src={localSystems[systemId].icon} alt={`${systemId} logo`} />
	//                             <span className="local-id__id">{id}</span>
	//                             <span className="wedge" />
	//                             <span
	//                                 className="local-id__unlink"
	//                                 onClick={() => unlinkIdentity(systemId, id)}
	//                             >{localSystems[systemId].icon}</span>
	//                             <img className="identities__check" alt="check" src={`${resourcesBaseUri}/green-check.svg`} />
	//                         </div>
	//                     ))}
	//                 </div>
	//             ))}
	//         </div>
	//     </div>
	// );
// }

export default MainPage;